/**
 * App-level game events: what one use case announces for others that come later to react to, without either knowing
 * the other (the season pass awarding pass XP for a Daily Road run, say). Synchronous and in-process; a listener that
 * throws is logged by the caller's stack, never swallowed here. The game uses the shared `gameEvents`; tests make their
 * own hub.
 */

/** A finished Daily Road run (MP-20), announced once per run. Pass XP and the like should key on `rewarded`. */
export interface DailyRoadCompleted {
  /** The UTC day number of the road. */
  readonly day: number;
  /** The campaign layout it borrowed. */
  readonly level: number;
  readonly won: boolean;
  readonly score: number;
  /** This run was the day's paid one (its first win). */
  readonly rewarded: boolean;
  /** It raised the day's best. */
  readonly newBest: boolean;
  /** The run's id (counted once). */
  readonly key: string;
  readonly at: number;
}

/** A Race Week run finished with a time (MP-21). Medal "" = finished but under bronze. */
export interface RaceFinished {
  /** ISO week key, e.g. "2026-W43". */
  readonly week: string;
  readonly level: number;
  /** Clear time in sim milliseconds (deterministic, from the tick count). */
  readonly ms: number;
  readonly medal: '' | 'bronze' | 'silver' | 'gold';
  readonly key: string;
  readonly at: number;
}

/** Any battle won (campaign, farm, event, Daily Road, challenge), announced once per run (MP-27). */
export interface BattleWon {
  /** "campaign" | "farm" | "event" | "dailyRoad" | "challenge" | "endless" | "iron" … (the run's mode id). */
  readonly mode: string;
  readonly level: number;
  /** Clear time in sim milliseconds. */
  readonly ms: number;
  readonly stars: number;
  readonly key: string;
  readonly at: number;
}

/** Every event by name. */
export interface GameEventMap {
  dailyRoadCompleted: DailyRoadCompleted;
  raceFinished: RaceFinished;
  battleWon: BattleWon;
}

export type GameEventName = keyof GameEventMap;

export class GameEventHub {
  private readonly listeners = new Map<GameEventName, ((e: never) => void)[]>();

  /** Listens to `name`; returns the unsubscribe. */
  on<K extends GameEventName>(name: K, listener: (e: GameEventMap[K]) => void): () => void {
    const list = this.listeners.get(name) ?? [];
    list.push(listener as (e: never) => void);
    this.listeners.set(name, list);
    return () => { const l = this.listeners.get(name); if (l) l.splice(l.indexOf(listener as (e: never) => void) >>> 0, 1); };
  }

  emit<K extends GameEventName>(name: K, e: GameEventMap[K]): void {
    for (const l of [...(this.listeners.get(name) ?? [])]) (l as (e: GameEventMap[K]) => void)(e);
  }
}

/** The game's hub. */
export const gameEvents = new GameEventHub();
